<template>
    <div class="card p-shadow-app">
        <h1>Controlo de estados Pats</h1>
        <Toast />

        <div class="p-fluid p-formgrid p-grid p-mb-3">

            <div class="p-field p-col-12 p-md-2">
                <label for="nopat">Número de PAT</label>
                <InputText
                    id="nopat"
                    v-model="filters.pat_id"
                    placeholder="Filtrar por número de PAT" />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="brand">Marca do Equipamento</label>
                <Dropdown
                    id="brand"
                    v-model="filters.brand"
                    :options="brands"
                    placeholder="Selecione a marca"
                    showClear
                />
            </div>

            <div class="p-field p-col-12 p-md-3">
                <label for="status">Estado Atual</label>
                <Dropdown
                    id="parentStatus"
                    v-model="filters.status"
                    :options="listStatuses"
                    optionLabel="field"
                    optionValue="field"
                    placeholder="Todos os Estados"
                    :showClear="true"
                    class="w-full p-ml-2"
                />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="confirmed">Estados confirmados</label>
                <Dropdown
                    id="confirmed"
                    v-model="filters.confirmed"
                    :options="[
                        { label: 'Todos', value: null },
                        { label: 'Confirmados', value: 1 },
                        { label: 'Não Confirmados', value: 0 },
                    ]"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Todos os Estados"
                    :showClear="true"
                    class="w-full p-ml-2"
                />
            </div>

            <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-end">
                <Button
                    label="Aplicar Filtros"
                    icon="pi pi-filter"
                    @click="fetchWithFilters"
                />
            </div>

            <div class="p-field p-col-12 p-md-1 p-d-flex p-ai-end">
                <Button
                    label="Limpar"
                    icon="pi pi-times"
                    class="p-button-secondary p-button-text"
                    @click="clearStoredFilters"
                />
            </div>
        </div>

        <DataTable
            :value="list"
            :loading="loading"
            :paginator="true"
            :rows="perPage"
            :rowsPerPageOptions="[10, 20, 50, 100]"
            responsiveLayout="scroll"
            class="p-datatable-sm"
            stripedRows
            :filters.sync="tableFilters"
            filterDisplay="menu"
            :lazy="true"
            :totalRecords="totalRecords"
            @filter="fetchData"
            @sort="fetchData"
            @page="fetchData"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrar de {first} a {last} de {totalRecords} dados"
        >
            <template #empty>
                <div class="p-text-center p-p-3">
                    <i class="pi pi-inbox" style="font-size: 3rem; color: #999;"></i>
                    <p class="p-mt-2">Nenhum estado encontrado</p>
                </div>
            </template>

            <Column field="pat_id" header="PAT" :sortable="false" style="min-width: 150px">
                <template #body="slotProps">
                    <span class="clickable-item" v-tooltip="'Ver Pat'" @click="goToPat(slotProps.data.pat_id)">
                        <strong>{{ slotProps.data.pat_id }}</strong>
                    </span>
                </template>
            </Column>

            <Column field="pat_equipment_brand" header="Marca do Equipamento" :sortable="false" style="min-width: 150px">
                <template #body="slotProps">
                    {{ slotProps.data.pat_equipment_brand || '-' }}
                </template>
            </Column>

            <Column field="old_status" header="Estado Antigo" :sortable="false" style="min-width: 180px">
                <template #body="slotProps">
                    <span class="p-badge p-component p-ml-2"
                      :style="{
                          backgroundColor: slotProps.data.old_status_color,
                          color: getReadableTextColor(slotProps.data.old_status_color)
                        }">
                        {{ slotProps.data.old_status || '-' }}
                    </span>
                </template>
            </Column>

            <Column field="status" header="Estado" :sortable="false" style="min-width: 200px">
                <template #body="slotProps">
                    <span class="p-badge p-component p-ml-2"
                      :style="{
                          backgroundColor: slotProps.data.status_color,
                          color: getReadableTextColor(slotProps.data.status_color)
                        }">
                        {{ slotProps.data.status || '-' }}
                    </span>
                </template>
            </Column>

            <Column field="created_at" header="Data" :sortable="false" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.created_at) || '-' }}
                </template>
            </Column>

            <Column field="confirmed_by_name" header="Confirmado Por" :sortable="false" style="min-width: 120px">
                <template #body="slotProps">
                    {{ slotProps.data.confirmed_by_name || '-' }}
                </template>
            </Column>

            <Column field="confirmed_at" header="Data Confirmada" :sortable="false" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.confirmed_at) || '-' }}
                </template>
            </Column>

            <Column header="Ações" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="p-d-flex p-gap-2">
                        <Button
                            icon="pi pi-eye"
                            class="p-button-rounded p-button-info p-button-text"
                            v-tooltip.top="'Ver Detalhes'"
                            @click="viewDetails(slotProps.data)"
                        />

                        <Button
                            v-if="!slotProps.data.confirmed_at"
                            icon="pi pi-check"
                            class="p-button-rounded p-button-success p-button-text"
                            v-tooltip.top="'Confirmar Alteração de Estado'"
                            @click="askForConfirm(slotProps.data)"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <ConfirmDialog group="statusControlPatsConfirmDialog" />

        <!-- View Details Dialog -->
        <Dialog
            :visible.sync="detailsDialog"
            :style="{ width: '800px' }"
            :header="`Detalhes - ${selectedRow?.pat_id || ''}`"
            :modal="true"
            :maximizable="true"
        >
            <div v-if="selectedRow" class="p-fluid">
                <div class="p-grid">
                    <div class="p-col-12 p-md-12">
                        <dl>
                            <dt>Tarefa:</dt>
                            <dd>
                                <strong>{{ selectedRow.task_id }}</strong><br />
                            </dd>
                            <dt>Técnico:</dt>
                            <dd>{{ selectedRow.created_by_name || '-' }}</dd>
                            <dt>Início deslocação:</dt>
                            <dd>{{ selectedRow.task_init_hour || '-' }}</dd>
                            <dt>Check-in:</dt>
                            <dd>{{ selectedRow.task_check_in || '-' }}</dd>
                            <dt>Check-out:</dt>
                            <dd>{{ selectedRow.task_check_out || '-' }}</dd>
                            <dt>Data:</dt>
                            <dd>{{ formatDate(selectedRow.created_at) || '-' }}</dd>
                            <dt>Problema:</dt>
                            <dd>{{ selectedRow.pat_problem || '-' }}</dd>
                            <dt>Resumo:</dt>
                            <dd>
                                <strong>{{ selectedRow.pat_resume || '-' }}</strong><br />
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button
                    label="Ver PAT"
                    icon="pi pi-box"
                    class="p-button p-button-info"
                    @click="goToPat(selectedRow.pat_id)"
                />

                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="detailsDialog = false"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import chroma from "chroma-js";
import technicalManagementService from '@/services/technical-management.service';
import equipmentService from '@/services/equipment.service';
import PatService from '@/services/pat.service';
import { FilterMatchMode } from 'primevue/api';
import ConfirmDialog from 'primevue/confirmdialog';
import { mapActions } from 'vuex';

export default {
    name: "StatusControlPats",
    storageKey: "status_control_pats_filters",
    components: {
        ConfirmDialog
    },
    watch: {
        'filters.confirmed'() {
            this.persistFilters();
        },
        'filters.pat_id'() {
            this.persistFilters();
        },
        'filters.brand'() {
            this.persistFilters();
        },
        'filters.status'() {
            this.persistFilters();
        }
    },

    data() {
        return {
            list: [],
            loading: false,
            totalRecords: 0,
            currentPage: 1,
            perPage: 50,

            tableFilters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            },

            detailsDialog: false,
            selectedRow: null,

            filters: {
                confirmed: 0,
                pat_id: null,
                brand: null,
                status: null,
            },
            brands: [],
            listStatuses: [],
        };
    },

    created() {
        this.restoreFilters();
        this.fetchAllOptions();
    },

    activated() {
        this.fetchWithFilters();
    },

    methods: {
        ...mapActions('technicalManagement', {
            fetchStatusControl: 'fetchStatusControl'
        }),
        persistFilters() {
            const payload = {
                confirmed: this.filters.confirmed,
                pat_id: this.filters.pat_id,
                brand: this.filters.brand,
                status: this.filters.status,
            };

            localStorage.setItem(this.$options.storageKey, JSON.stringify(payload));
        },
        restoreFilters() {
            const rawValue = localStorage.getItem(this.$options.storageKey);
            if (!rawValue) {
                return;
            }

            try {
                const savedFilters = JSON.parse(rawValue);
                this.filters.confirmed = savedFilters.confirmed ?? 0;
                this.filters.pat_id = savedFilters.pat_id ?? null;
                this.filters.brand = savedFilters.brand ?? null;
                this.filters.status = savedFilters.status ?? null;
            } catch (error) {
                localStorage.removeItem(this.$options.storageKey);
            }
        },
        clearStoredFilters() {
            this.filters.confirmed = 0;
            this.filters.pat_id = null;
            this.filters.brand = null;
            this.filters.status = null;
            localStorage.removeItem(this.$options.storageKey);
            this.fetchData();
        },
        async fetchAllOptions() {
            await Promise.all([
                this.fetchStatusControl(),
                this.fetchBrands(),
                this.fetchStatus(),
                this.fetchData()
            ]);
        },
        async fetchStatus() {
            try {
                this.listStatuses = await PatService.getAllStatus();
            } catch (error) {
                this.$toast.add({
                severity: "error",
                summary: "Erro",
                detail: "Falha ao carregar estados principais.",
                life: 3000,
                });
            }
        },
        async fetchBrands() {
            try {
                const response = await equipmentService.getAllEquipmentsBrands();

                if (!response || response.length === 0) return;

                this.brands = response;
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar marcas',
                    life: 3000
                });
            }
        },
        async fetchData(event = {}) {
            try {
                this.loading = true;

                const page = event.page ? event.page + 1 : 1;

                const params = {
                    page,
                    per_page: event.rows ? event.rows : this.perPage
                };

                if (this.filters.confirmed !== null && this.filters.confirmed !== undefined) {
                    params.confirmed = this.filters.confirmed;
                }

                if (this.filters.pat_id) {
                    params.pat_id = this.filters.pat_id;
                }

                if (this.filters.brand) {
                    params.brand = this.filters.brand;
                }

                if (this.filters.status) {
                    params.status = this.filters.status;
                }

                const response = await technicalManagementService.getPatStatusControl(params);

                this.list = response.data;
                this.totalRecords = response.meta.total;
                this.currentPage = response.meta.current_page;

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar dados',
                    life: 3000
                });
            } finally {
                this.loading = false;
            }
        },

        viewDetails(row) {
            this.selectedRow = row;
            this.detailsDialog = true;
        },

        formatDate(dateString) {
            if (!dateString) return '-';

            const date = new Date(dateString);
            return date.toLocaleDateString('pt-PT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },

        askForConfirm(row) {
            this.$confirm.require({
                group: 'statusControlPatsConfirmDialog',
                message: `Tem a certeza que deseja confirmar o estado do PAT ${row.pat_id}?`,
                header: 'Confirmação de Estado',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sim',
                rejectLabel: 'Não',
                accept: async () => await this.confirm(row),
                reject: () => {
                    return;
                },
            });
        },

        async confirm(row) {
            try {
                this.loading = true;

                const response = await technicalManagementService.confirmPatStatusControl(row.id);

                if (response.status === 'success') {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'Estado confirmado com sucesso',
                        life: 3000
                    });

                    await this.fetchData();
                    await this.fetchStatusControl();
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: response.message || 'Erro ao confirmar estado',
                        life: 3000
                    });
                }

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar dossiês',
                    life: 3000
                });
            } finally {
                this.loading = false;
            }
        },

        goToPat(patId) {
            this.$router.push(`/pats/${patId}`);
        },

        fetchWithFilters() {
            this.persistFilters();
            this.fetchData();
        },

        getReadableTextColor(backgroundColor) {
            const whiteContrast = chroma.contrast(backgroundColor, "#ffffff");
            const blackContrast = chroma.contrast(backgroundColor, "#000000");
            return whiteContrast > blackContrast ? "#ffffff" : "#000000";
        },
    }
};
</script>

<style scoped>
.card {
    padding: 2rem;
}

h1 {
    margin-bottom: 1.5rem;
    color: #495057;
}

.p-field label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

dl {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 0.5rem;
    margin: 0;
}

dt {
    font-weight: 600;
    color: #6c757d;
}

dd {
    margin: 0;
}

.confirmation-content {
    display: flex;
    align-items: center;
    padding: 1rem 0;
}

pre {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
}

.p-gap-2 {
    gap: 0.5rem;
}
</style>
